/* Form di editing adattivo per un singolo step del flow. Su ogni modifica
   chiama onApply con lo step aggiornato (clone). Non ricostruisce sé stesso:
   il chiamante deve aggiornare il diagramma senza rifare il form (per non
   perdere il focus mentre si digita). */
import type { DiagramDoc, FlowStep } from "@fullsteps/shared";
import { el } from "../render/svg.js";

type AnyStep = Record<string, unknown> & { t: FlowStep["t"] };

export function buildStepForm(
  step: FlowStep,
  doc: DiagramDoc,
  onApply: (s: FlowStep) => void,
): HTMLElement {
  const draft = structuredClone(step) as AnyStep;
  const form = el("div", "ed-form");
  const apply = (): void => onApply(structuredClone(draft) as unknown as FlowStep);

  const field = (label: string, control: HTMLElement): void => {
    const f = el("label", "ed-field");
    f.appendChild(el("span", "ed-label", label));
    f.appendChild(control);
    form.appendChild(f);
  };

  const textInput = (value: string | undefined, set: (v: string) => void, ph = ""): HTMLInputElement => {
    const i = el("input", "ed-input") as HTMLInputElement;
    i.type = "text";
    i.value = value ?? "";
    i.placeholder = ph;
    i.addEventListener("input", () => {
      set(i.value);
      apply();
    });
    return i;
  };
  const textArea = (value: string, set: (v: string) => void): HTMLTextAreaElement => {
    const t = el("textarea", "ed-input ed-textarea") as HTMLTextAreaElement;
    t.value = value;
    t.rows = 3;
    t.addEventListener("input", () => {
      set(t.value);
      apply();
    });
    return t;
  };
  const partSelect = (value: string, set: (v: string) => void): HTMLSelectElement => {
    const s = el("select", "ed-input") as HTMLSelectElement;
    for (const p of doc.participants) {
      const o = el("option", undefined, p.name) as HTMLOptionElement;
      o.value = p.id;
      if (p.id === value) o.selected = true;
      s.appendChild(o);
    }
    s.addEventListener("change", () => {
      set(s.value);
      apply();
    });
    return s;
  };

  switch (draft.t) {
    case "phase": {
      field("Titolo", textInput(draft.title as string, (v) => (draft.title = v)));
      field("Sottotitolo", textInput(draft.sub as string, (v) => (draft.sub = v || undefined)));
      break;
    }
    case "note": {
      field("Sopra", partSelect(draft.over as string, (v) => (draft.over = v)));
      field("Testo", textArea(draft.text as string, (v) => (draft.text = v)));
      break;
    }
    case "msg": {
      field("Da", partSelect(draft.from as string, (v) => (draft.from = v)));
      field("A", partSelect(draft.to as string, (v) => (draft.to = v)));
      field("Testo", textArea(draft.text as string, (v) => (draft.text = v)));
      field("Sync (contemporanei)", textInput(draft.sync as string, (v) => (draft.sync = v || undefined), "es. sy-cantiere"));

      // documenti citati
      const docsWrap = el("div", "ed-chips");
      const selected = new Set<string>((draft.docs as string[] | undefined) ?? []);
      for (const d of doc.docs) {
        const b = el("button", "ed-chip", d.short) as HTMLButtonElement;
        b.type = "button";
        b.title = d.label;
        b.setAttribute("aria-pressed", String(selected.has(d.id)));
        b.addEventListener("click", () => {
          if (selected.has(d.id)) selected.delete(d.id);
          else selected.add(d.id);
          b.setAttribute("aria-pressed", String(selected.has(d.id)));
          draft.docs = selected.size ? [...selected] : undefined;
          apply();
        });
        docsWrap.appendChild(b);
      }
      field("Documenti", docsWrap);

      // scadenza
      const dl = draft.deadline as { short: string; label: string } | undefined;
      const dlWrap = el("div", "ed-inline");
      const chk = el("input") as HTMLInputElement;
      chk.type = "checkbox";
      chk.checked = !!dl;
      const shortI = textInput(dl?.short, (v) => {
        (draft.deadline as { short: string }).short = v;
      }, "≤ 2 gg");
      const labelI = textInput(dl?.label, (v) => {
        (draft.deadline as { label: string }).label = v;
      }, "entro 2 giorni…");
      const applyDisabled = (): void => {
        shortI.disabled = !chk.checked;
        labelI.disabled = !chk.checked;
      };
      chk.addEventListener("change", () => {
        applyDisabled();
        if (chk.checked && !draft.deadline) draft.deadline = { short: shortI.value || "", label: labelI.value || "" };
        if (!chk.checked) draft.deadline = undefined;
        apply(); // apply solo su interazione reale, non alla costruzione del form
      });
      applyDisabled();
      const cl = el("label", "ed-check");
      cl.append(chk, document.createTextNode(" scadenza"));
      dlWrap.append(cl);
      field("Scadenza", dlWrap);
      field("· etichetta breve", shortI);
      field("· etichetta estesa", labelI);
      break;
    }
    case "act":
    case "deact":
    case "destroy": {
      field("Attore", partSelect(draft.a as string, (v) => (draft.a = v)));
      break;
    }
    case "create": {
      field("Attore", partSelect(draft.a as string, (v) => (draft.a = v)));
      const chk = el("input") as HTMLInputElement;
      chk.type = "checkbox";
      chk.checked = !!draft.inline;
      chk.addEventListener("change", () => {
        draft.inline = chk.checked || undefined;
        apply();
      });
      const cl = el("label", "ed-check");
      cl.append(chk, document.createTextNode(" inline (sulla riga del messaggio seguente)"));
      field("Modalità", cl);
      break;
    }
    case "block": {
      const s = el("select", "ed-input") as HTMLSelectElement;
      for (const k of ["alt", "opt", "loop"] as const) {
        const o = el("option", undefined, k.toUpperCase()) as HTMLOptionElement;
        o.value = k;
        if (k === draft.kind) o.selected = true;
        s.appendChild(o);
      }
      s.addEventListener("change", () => {
        draft.kind = s.value;
        apply();
      });
      field("Tipo", s);
      field("Condizione", textInput(draft.cond as string, (v) => (draft.cond = v)));
      break;
    }
    case "else": {
      field("Condizione", textInput(draft.cond as string, (v) => (draft.cond = v)));
      break;
    }
    case "end": {
      form.appendChild(el("p", "hint", "Fine di un blocco: nessun campo da modificare."));
      break;
    }
  }
  return form;
}
